import _sortBy from 'lodash.sortby';
import {
  WorkflowGraphNode,
  DropAreaWorkflowGraphNode,
  WorkflowGraph,
  BranchWorkflowGraphEdge,
  UnitWorkflowGraphNode,
  ActionWorkflowGraphNode,
} from 'cio/utils/visual-workflow-builder/workflow-graph';
import { estimateSize, LAYER_HEIGHT, NODE_SIZES } from 'cio/utils/visual-workflow-builder/layout/size';
import ActionModel, { BRANCH_TYPES } from 'cio/models/action';
import { Edge } from 'cio/models/campaign';

export function prepareGraphNodeForAction(action: ActionModel): ActionWorkflowGraphNode {
  return {
    id: action.id,
    type: action.type,
    typeCategory: 'action',
    width: 0,
    height: 0,
    x: 0,
    y: 0,
    action,
  };
}

export function addGraphNodesAndEdges(actions: ActionModel[], edges: Edge[], workflowGraph: WorkflowGraph) {
  actions.forEach((action) => {
    workflowGraph.addNode(prepareGraphNodeForAction(action));
  });

  edges.forEach((edge) => {
    if (edge.type === 'branch') {
      workflowGraph.addEdge(edge.from, edge.to, { type: edge.type, index: edge.index as number });
    } else if (edge.type === 'continue') {
      workflowGraph.addEdge(edge.from, edge.to, { type: edge.type });
    }
  });
}

// This function adds virtual nodes for a branch's path labels.
// Those are the labels that say "True"/"False" or what a multi-split's
// condition is.
export function addPathLabelNodes(workflowGraph: WorkflowGraph) {
  let branchNodes = Object.values(workflowGraph.nodes).filter(
    (node) => node.typeCategory === 'action' && BRANCH_TYPES.includes(node.type),
  );

  branchNodes.forEach((n) => {
    let outgoingEdges = workflowGraph.outEdges[n.id] as BranchWorkflowGraphEdge[];

    // If the branch only has one outgoing edge, we're not going to add path
    // labels. For example: a wait until branch with only one condition
    if (outgoingEdges.length <= 1) {
      return;
    }

    _sortBy(outgoingEdges, ['index']).forEach((edge) => {
      let id = `v_pl_${n.id}_${edge.index}`;
      let node: WorkflowGraphNode = {
        id,
        type: 'virtual_path_label',
        typeCategory: 'virtual',
        width: NODE_SIZES.virtual_path_label.width,
        height: NODE_SIZES.virtual_path_label.height,
        x: 0,
        y: 0,
        index: edge.index,
        branchId: n.id,
        branch: workflowGraph.campaign.branchesMap[n.id],
      };
      workflowGraph.addNode(node);

      workflowGraph.addEdge(edge.from, id, {
        type: 'branch',
        index: edge.index,
        branchId: n.id,
        originalEdge: edge.originalEdge ?? edge,
      });
      workflowGraph.addEdge(id, edge.to, {
        type: 'branch',
        index: edge.index,
        branchId: n.id,
        originalEdge: edge.originalEdge ?? edge,
      });
      workflowGraph.removeEdge(edge.id);
    });
  });
}

// This adds virtual nodes for where a branch action's paths reconnect. We add
// these extra nodes so that we can later place drop areas in the right places.
// Without these extra nodes, the structure would look a bit more flat.
// Imagine this scenario:
//     [ Trigger ]
//          |
//     [ 1 Branch ]
//          |
//  -----------------
//  |               |
//  |          [ 2 Branch ]
//  |               |
//  |       -----------------
//  |       |               |
//  |       -----------------
//  |               |
//  -----------------
//          |
//     [ 3 Exit ]
//
// Technically, both branch actions 1 and 2 "reconnect" to the Exit at the
// bottom. But adding extra reconnect nodes for individual branches allows us
// to stagger the reconnection, so that there's a gap under branch 2.
// Without these reconnect nodes we'd end up with this instead:
//     [ Trigger ]
//          |
//     [ 1 Branch ]
//          |
//  -----------------
//  |               |
//  |          [ 2 Branch ]
//  |               |
//  |       -----------------
//  |       |               |
//  -------------------------
//          |
//     [ 3 Exit ]
export function addReconnectNodes(workflowGraph: WorkflowGraph) {
  let { branchesMap, branchRejoinPointsMap, actionDepths } = workflowGraph.campaign;

  let branchNodesByDeepest = Object.keys(branchRejoinPointsMap).sort((a, b) => actionDepths[b] - actionDepths[a]);
  branchNodesByDeepest.forEach((n) => {
    let rejoinPoint = branchRejoinPointsMap[n];
    if (!rejoinPoint) {
      return;
    }

    let edgesIncomingToRejoinPoint = [...workflowGraph.inEdges[rejoinPoint]];

    // If there's only one edge pointing to this node, we don't need any extra
    // treatment, as the paths are simple regular paths from action A to action
    // B (when a wait until action only has one condition, for example, though
    // it's technically a branch action)
    if (edgesIncomingToRejoinPoint.length <= 1) {
      return;
    }

    let branch = branchesMap[n];

    let lastNodes = branch.edges
      .map((e) => {
        if (e.nodes.length === 0) {
          return n;
        }

        return e.nodes[e.nodes.length - 1].id;
      })
      .compact();

    // only adjust edges leaving the current branch, so that we don't
    // accidentally change neighboring actions/branches
    edgesIncomingToRejoinPoint = edgesIncomingToRejoinPoint.filter(
      (e) => lastNodes.includes(e.from) || (e.branchId && lastNodes.includes(e.branchId)),
    );

    let areAllLastNodesExits = lastNodes.every((n) => {
      let action = workflowGraph.nodes[n];
      return action.type === 'exit_action';
    });

    let id = `v_r_${workflowGraph.virtualNodeIndex}`;
    let virtualNode: WorkflowGraphNode = {
      id,
      type: 'virtual_rejoin',
      typeCategory: 'virtual',
      width: NODE_SIZES.virtual_rejoin.width,
      height: NODE_SIZES.virtual_rejoin.height,
      x: 0,
      y: 0,
      branch,
      branchId: n,
    };

    workflowGraph.addNode(virtualNode);
    workflowGraph.virtualNodeIndex++;

    let lastExitNodes = lastNodes.filter((n) => {
      let action = workflowGraph.nodes[n];
      return action.type === 'exit_action';
    });

    workflowGraph.addEdge(id, rejoinPoint, { type: 'continue', branchId: n });
    if (!areAllLastNodesExits) {
      let edgesIncomingToRejoinPointFromBranch = edgesIncomingToRejoinPoint.filter(
        (edge) =>
          lastNodes.includes(edge.from) ||
          (workflowGraph.nodes[edge.from]?.type === 'virtual_path_label' && edge.branchId === n) ||
          workflowGraph.nodes[edge.from]?.type === 'virtual_rejoin',
      );

      edgesIncomingToRejoinPointFromBranch.forEach((edge) => {
        if (edge.type === 'branch') {
          workflowGraph.addEdge(edge.from, id, {
            type: 'branch',
            index: edge.index,
            branchId: n,
            originalEdge: edge.originalEdge ?? edge,
          });
        } else {
          workflowGraph.addEdge(edge.from, id, {
            type: 'continue',
            branchId: n,
            originalEdge: edge.originalEdge ?? edge,
          });
        }
        workflowGraph.removeEdge(edge.id);
      });
    }

    // These hidden edges are necessary for the algorithm to create more compact
    // results. Without them, the results might be wider/uglier than we want.
    // The edges are later removed when adding dummy nodes
    lastExitNodes.forEach((n) => {
      workflowGraph.addEdge(n, id, {
        type: 'virtual',
      });
    });
  });
}

// After the previous step, we perform this extra step to remove some rejoin
// nodes that aren't necessary. For example, in a nested scenario like this one:
//      [ Trigger ]
//           |
//      [ 1 Branch ]
//           |
//   -----------------
//   |               |
//   |          [ 2 Branch ]
//   |               |
//   |       -----------------
//   |       |               |
//   |       |          [ 3 Branch ]
//   |       |               |
//   |       |          ------------
//   |       |          |          |
//   |       |     [ 4 Exit ]  [ 5 Exit ]
//   |       |
//   ---------
//       |
//   [ 3 Exit ]
//
// The previous step would actually add an extra reconnect node for Branch 2:
//     [ Trigger ]
//          |
//     [ 1 Branch ]
//          |
//  -----------------
//  |               |
//  |          [ 2 Branch ]
//  |               |
//  |       -----------------
//  |       |               |
//  |       |          [ 3 Branch ]
//  |       |               |
//  |       |          ------------
//  |       |          |          |
//  |       |     [ 4 Exit ]  [ 5 Exit ]
//  |       |
//  |       ---------
//  |               | <- reconnect node here
//  -----------------
//          |
//     [ 3 Exit ]
//
// Those extra rejoin nodes are entirely unnecessary, so this step removes them.
export function removeSingularRejoinNodes(workflowGraph: WorkflowGraph) {
  let rejoinNodes = Object.values(workflowGraph.nodes).filter((a) => a.type === 'virtual_rejoin');

  rejoinNodes.forEach((n) => {
    let incomingEdges = [...workflowGraph.inEdges[n.id]];

    if (incomingEdges.every((e) => e.type === 'virtual')) {
      let outgoingEdges = workflowGraph.outEdges[n.id];
      if (outgoingEdges.length === 1) {
        let [outgoingEdge] = outgoingEdges;

        incomingEdges.forEach((e) => {
          if (e.type === 'branch') {
            workflowGraph.addEdge(e.from, outgoingEdge.to, {
              type: e.type,
              originalEdge: e.originalEdge ?? e,
              index: e.index,
            });
          } else {
            workflowGraph.addEdge(e.from, outgoingEdge.to, {
              type: e.type,
              originalEdge: e.originalEdge ?? e,
            });
          }
          workflowGraph.removeEdge(e.id);
        });

        workflowGraph.removeEdge(outgoingEdge.id);
      }

      workflowGraph.removeNode(n.id);
      return;
    }

    let incomingContinueEdges = incomingEdges.filter((e) => e.type === 'continue' || e.type === 'branch');
    let outgoingEdges = workflowGraph.outEdges[n.id];

    if (incomingContinueEdges.length === 1) {
      let [incomingEdge] = incomingContinueEdges;

      if (outgoingEdges.length === 1) {
        let [outgoingEdge] = outgoingEdges;

        workflowGraph.addEdge(incomingEdge.from, outgoingEdge.to, {
          type: 'continue',
          originalEdge: incomingEdge.originalEdge ?? incomingEdge,
        });
        workflowGraph.removeEdge(incomingEdge.id);

        let remainingIncomingEdges = workflowGraph.inEdges[n.id].filter(
          (e) => e.type !== 'continue' && e.type !== 'branch',
        );
        remainingIncomingEdges.forEach((e) => {
          if (e.type === 'branch') {
            workflowGraph.addEdge(e.from, outgoingEdge.to, {
              type: e.type,
              originalEdge: e.originalEdge ?? e,
              index: e.index,
            });
          } else {
            workflowGraph.addEdge(e.from, outgoingEdge.to, {
              type: e.type,
              originalEdge: e.originalEdge ?? e,
            });
          }
          workflowGraph.removeEdge(e.id);
        });

        workflowGraph.removeEdge(outgoingEdge.id);
        workflowGraph.removeNode(n.id);
      }
    }
  });
}

// This adds extra virtual nodes between all other nodes, which will be used in
// the VWB as the "drop areas", i.e. the elements where an action can be dropped
// on to be moved or added.
export function addDropNodes(workflowGraph: WorkflowGraph) {
  let { parentBranchMap, branchesMap } = workflowGraph.campaign;
  Object.values(workflowGraph.edges).forEach((edge) => {
    if (edge.type === 'virtual') {
      return;
    }

    let sourceAction = workflowGraph.nodes[edge.from];
    let targetAction = workflowGraph.nodes[edge.to];

    if (targetAction.type === 'virtual_path_label') {
      return;
    }

    let id = `v_dr__${sourceAction.id}__${targetAction.id}`;
    let virtualAction = {
      id,
      type: 'virtual_drop_area',
      typeCategory: 'virtual',
      width: NODE_SIZES.virtual_drop_area.width,
      height: NODE_SIZES.virtual_drop_area.height,
      x: 0,
      y: 0,
      source: sourceAction,
      target: targetAction,
      edge: edge.originalEdge ?? edge,
    } as DropAreaWorkflowGraphNode;

    if (sourceAction?.branchId) {
      virtualAction.branchId = sourceAction.branchId;
      virtualAction.branch = sourceAction.branch;
    } else if (sourceAction?.id) {
      // branchIds are not assigned to branch actions - they're assigned to
      // nodes like the path label nodes, to point to the source branch they
      // belong to.
      // In case the source action here is the branch action itself, we need to
      // assign the correct branchId.
      // This can happen for Wait Until actions with only one condition, because
      // we don't add path labels for that one branch.
      if (sourceAction?.typeCategory === 'action' && BRANCH_TYPES.includes(sourceAction?.type)) {
        virtualAction.branchId = sourceAction.id;
        virtualAction.branch = branchesMap[sourceAction.id];
      } else {
        let parent = parentBranchMap[sourceAction.id];

        if (parent) {
          virtualAction.branchId = parent;
          virtualAction.branch = branchesMap[parent];
        }
      }
    }

    workflowGraph.addNode(virtualAction);

    if (edge.type === 'branch') {
      workflowGraph.addEdge(edge.from, id, {
        type: edge.type,
        index: edge.index,
        originalEdge: edge.originalEdge ?? edge,
      });
      workflowGraph.addEdge(id, edge.to, {
        type: edge.type,
        index: edge.index,
        originalEdge: edge.originalEdge ?? edge,
      });
    } else {
      workflowGraph.addEdge(edge.from, id, {
        type: edge.type,
        originalEdge: edge.originalEdge ?? edge,
      });
      workflowGraph.addEdge(id, edge.to, {
        type: edge.type,
        originalEdge: edge.originalEdge ?? edge,
      });
    }
    workflowGraph.removeEdge(edge.id);
  });
}

// The layout algorithm moves nodes around X/Y coordinates, but it doesn't know
// whether an edge crosses a given coordinate.
// In a scenario like
//          [ Trigger ]
//               |
//          [ 1 Branch ]
//               |
//       -------------------
//       |                 |
//       |            [ 2 Branch ]
//       |                 |
//       |         -----------------
//       |         |               |
//       |    [ 3 Action ]         |
//       |         |               |
//       |         -----------------
//       |                 |
//       ------------------
//               |
//          [ 4 Exit ]
//
// the algorithm wouldn't know that the empty edge of Branch 1 exists during
// collision checks, so we'd end up with something like (not 100% accurate):
//          [ Trigger ]
//               |
//          [ 1 Branch ]
//               |
//       -----------------
//  [path label]     [path label]
//       |               |
//       |          [ 2 Branch ]
//       |               |
//       |   -------------
//       |   |        |
//     [ 3 Action ]   |
//       |            |
//       --------------
//               |
//          [ 4 Exit ]
//
// Essentially, another branch action's child actions could end up crossing a
// long path.
// In order to avoid this, at each layer for long edges we add a fake node,
// which will then be recognized for collision checks.
export function addDummyNodes(workflowGraph: WorkflowGraph) {
  Object.keys(workflowGraph.nodes).forEach((id) => {
    let nodeRank = workflowGraph.ranks[id];
    let outgoingEdges = workflowGraph.outEdges[id] ?? [];

    let node = workflowGraph.nodes[id];
    let isExit =
      node.type === 'exit_action' ||
      (node.type === 'virtual_unit' && node.originalNode.type === 'exit_action' && node.isLastIndex);

    outgoingEdges.forEach((edge) => {
      // If the edge is an exit, ignore all outgoing edges - they were fake
      // edges needed for proper ranking of reconnect and unit nodes
      if (isExit) {
        workflowGraph.removeEdge(edge.id);
        return;
      }

      let nextId = edge.to;
      let rankDiff = workflowGraph.ranks[nextId] - nodeRank;

      if (rankDiff > 1) {
        let newDummyNodes = [...new Array(rankDiff - 1)].map((_, i) => {
          let node = {
            id: `v_d_${id}_${i}`,
            type: 'virtual_dummy',
            isLastIndex: i === rankDiff - 2,
          };

          return node;
        });

        let originalGraphNode = workflowGraph.nodes[edge.from];
        newDummyNodes.forEach((node, i) => {
          workflowGraph.ranks[node.id] = nodeRank + i + 1;

          workflowGraph.addNode({
            id: node.id,
            type: 'virtual_dummy',
            typeCategory: 'virtual',
            width: NODE_SIZES.virtual_dummy.width,
            height: NODE_SIZES.virtual_dummy.height,
            x: 0,
            y: 0,
            originalNode: originalGraphNode,
            isLastIndex: node.isLastIndex,
          });
        });

        [...new Array(rankDiff)].forEach((_, i) => {
          let from = '';
          let to = '';

          if (i === 0) {
            from = edge.from;
            to = newDummyNodes[i].id;
          } else if (i === rankDiff - 1) {
            from = newDummyNodes[i - 1].id;
            to = edge.to;
          } else {
            from = newDummyNodes[i - 1].id;
            to = newDummyNodes[i].id;
          }

          if (edge.type === 'branch') {
            workflowGraph.addEdge(from, to, {
              type: edge.type,
              index: edge.index,
              branchId: edge.branchId,
              originalEdge: edge.originalEdge,
            });
          } else {
            workflowGraph.addEdge(from, to, {
              type: edge.type,
              branchId: edge.branchId,
              originalEdge: edge.originalEdge,
            });
          }
        });

        workflowGraph.removeEdge(edge.id);
      }
    });
  });
}

// Actions can have different heights and we want the layout algorithm to be
// aware of that fact.
// Part of the layout algorithm is assigning a rank (or layer) to each action,
// from top to bottom. If we simply follow edges and assign layers, a tall
// action might end up stretching a layer, leading to unbalanced layers:
//
//            [ Trigger ]
//                 |
//            [ 1 Branch ]
//                 |
//         -----------------
//         |               |
//    [ 2 Action ]   [              ]
//         |         [ 3 Tall Split ]
//         |         [              ]
//         |               |
//    [ 4 Action ]         |
//         |               |
//         -----------------
//                 |
//            [ 5 Exit ]
// In this scenario, Action 2 and Split 3 would end up in the same layer.
// However, the split is taller than the action, so the action would have a long
// gap after it connecting it to action 4.
// We want to try and keep those gaps/edges between actions a uniform length, so
// we add what we call "unit" nodes.
// This function replaces all actions in the workflow with fake actions that are
// all a uniform height.
// So if we say a regular action is 10px tall, and a split action is 30px tall,
// we could replace the regular action with 1 unit node of height 10, and the
// split action with 3 unit nodes of height 10.
// We could instead set the unit node height to 5px,for example, and then a
// regular action would get replaced by 2 unit nodes, and a split action would
// get replaced by 6 unit nodes.
// The unit node height needs to be small enough to produce decent results, but
// we can't set it too low (like 1px), because then we'd end up inserting
// thousands of unit nodes, which would significantly slow down the overall
// layout algorithm.
//
// Once the unit nodes are inserted, the layout algorithm moves them around so
// that there are no collisions. At the end of the algorithm we remove the unit
// nodes and replace them with the original actions at the unit nodes'
// coordinates.
export function createUnitNodesGraph(workflowGraph: WorkflowGraph, currentTab: string) {
  let unitNodesWorkflowGraph = new WorkflowGraph(workflowGraph.campaign);

  let unitNodeId = 0;
  let unitNodesMap: Record<string, UnitWorkflowGraphNode[]> = {};

  Object.keys(workflowGraph.nodes).forEach((node) => {
    let originalGraphNode = workflowGraph.nodes[node];

    let size = estimateSize(originalGraphNode, currentTab, workflowGraph.campaign);

    let nodeHeight = size.height;
    let nodeHeightInLayers = Math.ceil(nodeHeight / LAYER_HEIGHT);

    let baseUnitNode: UnitWorkflowGraphNode = {
      id: '',
      type: 'virtual_unit',
      typeCategory: 'virtual',
      width: size.width,
      height: LAYER_HEIGHT,
      x: 0,
      y: 0,
      originalNode: originalGraphNode as WorkflowGraphNode,
      index: 0,
      isLastIndex: false,
    };

    let unitNodes: UnitWorkflowGraphNode[] = [];

    [...new Array(nodeHeightInLayers)].forEach((_, i) => {
      let id = `u_${unitNodeId}`;
      unitNodeId++;

      let unitNode = {
        ...baseUnitNode,
        id,
        index: i,
        isLastIndex: i === nodeHeightInLayers - 1,
      };

      unitNodesWorkflowGraph.addNode(unitNode);
      unitNodes.push(unitNode);
    });
    unitNodesMap[node] = unitNodes;

    unitNodes.slice(1).forEach((unitNode, i) => {
      let previousUnitNode = unitNodes[i];
      unitNodesWorkflowGraph.addEdge(previousUnitNode.id, unitNode.id, {
        type: 'unit',
      });
    });
  });

  Object.values(workflowGraph.edges).forEach((edge) => {
    let unitNodesForSource = unitNodesMap[edge.from];
    let unitNodesForTarget = unitNodesMap[edge.to];

    if (edge.type === 'branch') {
      unitNodesWorkflowGraph.addEdge(unitNodesForSource[unitNodesForSource.length - 1].id, unitNodesForTarget[0].id, {
        type: edge.type,
        index: edge.index,
        branchId: edge.branchId,
        originalEdge: edge.originalEdge,
      });
    } else {
      unitNodesWorkflowGraph.addEdge(unitNodesForSource[unitNodesForSource.length - 1].id, unitNodesForTarget[0].id, {
        type: edge.type,
        branchId: edge.branchId,
        originalEdge: edge.originalEdge,
      });
    }
  });

  return unitNodesWorkflowGraph;
}
